import { DayOfTheWeek } from '@/models/DayOfTheWeek';
import { Geolocation } from '@/models/Geolocation';
import { OrderPredictionAlgorithm } from '@/models/OrderPrediction';
import { Retailer } from '@/models/Retailer';
import { Status } from '@/models/Status';
import { Time } from '@/models/Time';

type customData = { [key: string]: string };
type dynamicFields = { [key: string]: string };

export enum StoreAssociation {
  RETAILER = 'retailer',
  ORGANIZATION = 'organization',
}

export interface Store {
  id: string;
  code: string;
  organizationId: string;
  retailerId: string;
  customerCode: string;
  moq: number;
  name: string;
  email: string;
  externalCode?: string;
  contactCountryCode: string;
  contactNumber: string;
  description: string;
  currencyCode: string;
  status: Status;
  showVat: boolean;
  orderPrediction: StoreOrderPrediction;
  addresses: StoreAddress[];
  receivingWindows: StoreReceivingWindow[];
  createdAt: Date;
  customData: customData;
  dynamicFields: dynamicFields;
  retailer?: Retailer;
}

export enum StoreAddressType {
  BUSINESS = 'business',
  BILLING = 'billing',
  DELIVERY = 'delivery',
  UNKNOWN = 'unknown',
}

export interface StoreAddress {
  id?: string;
  storeId: string;
  type: StoreAddressType;
  building: string;
  street1: string;
  street2: string;
  postalCode: string;
  region: string;
  city: string;
  countryCode: string;
  location: Geolocation;
  additionalDescription: string;
}

export interface StoreReceivingWindow {
  id?: string;
  storeId?: string;
  day: DayOfTheWeek;
  startTime: Time;
  endTime: Time;
}

export interface Stores {
  items: Store[];
  totalCount?: number;
  estimatedTotalCount?: number;
}

export interface StoreMetrics {
  storeCount: number;
  activeStoreCount: number;
}

export interface StoreCreate {
  code: string;
  retailerId: string;
  name: string;
  email: string;
  externalCode?: string;
  contactNumber: string;
  description: string;
  currencyCode: string;
  status: Status;
  addresses: StoreAddress[];
  receivingWindows: StoreReceivingWindow[];
  orderPrediction: StoreOrderPrediction;
  customData: customData;
  dynamicFields: dynamicFields;
}

export interface StoreUpdate {
  id: string;
  code: string;
  retailerId: string;
  name: string;
  email: string;
  externalCode?: string;
  contactNumber: string;
  description: string;
  currencyCode: string;
  status: Status;
  addresses: StoreAddress[];
  receivingWindows: StoreReceivingWindow[];
  orderPrediction: StoreOrderPrediction;
  customData: customData;
  dynamicFields: dynamicFields;
}

export interface StoreOrderPrediction {
  enabled: boolean;
  algorithm?: OrderPredictionAlgorithm;
}

export function findStoreAddress(
  addresses?: StoreAddress[],
  addressTypes: StoreAddressType[] = [StoreAddressType.BUSINESS],
): StoreAddress | undefined {
  if (!addresses) {
    return;
  }
  for (const addressType of addressTypes) {
    const address = addresses.find((address) => address.type === addressType);
    if (address) {
      return address;
    }
  }
  return;
}
